import {useEffect} from 'react';
import {useLeafletContext} from '@react-leaflet/core';
import L from 'leaflet';
import {MAP_TILE_URLS} from '../services/mapTileUrls';
import {savedImageryCandidates,decodeSavedImagery} from '../services/offlineImagery';
import './OfflineImageryLayer.css';

export class SavedImageryTileLayer extends L.TileLayer {
 private work=new Map<HTMLElement,{url:string;cancel:()=>void;retry:(force?:boolean)=>void}>();
 private decoded=new Map<string,Promise<HTMLImageElement>>();
 constructor(url:string,options?:L.TileLayerOptions){
  super(url,options);
  this.on('tileunload',event=>{this.work.get(event.tile)?.cancel();this.work.delete(event.tile);});
 }
 protected _abortLoading(){
  // TileLayer's image-only implementation treats our DIV tiles' missing
  // `complete` property as an unfinished image and removes even painted parents
  // on every zoom. Let GridLayer retain parents until their replacements are
  // ready, then prune normally; tileunload cancels our pending image work.
 }
 protected createTile(coords:L.Coords,done:L.DoneCallback):HTMLElement{
  const tile=document.createElement('div'),started=performance.now();let image=document.createElement('img'),objectUrl:string|undefined,requestController:AbortController|undefined;
  tile.style.overflow='hidden';
  let cancelled=false,finished=false,ready=false,restoring=true,failed=false,pending=false,refreshQueued=false;
  const finish=(error?:Error)=>{if(!finished&&!cancelled){finished=true;done(error,tile);}};
  const report=(source:string)=>window.dispatchEvent(new CustomEvent('sonata-map-tile',{detail:{source,duration:performance.now()-started}}));
  const releaseUrl=()=>{if(objectUrl){URL.revokeObjectURL(objectUrl);objectUrl=undefined;}};
  const load=(refresh=false)=>{
   if(cancelled||pending)return;pending=true;
   image=document.createElement('img');image.alt='';image.decoding='async';image.style.cssText='width:100%;height:100%;max-width:none';
   image.onload=()=>{
    if(cancelled)return;pending=false;ready=true;failed=false;
    tile.replaceChildren(image);delete tile.dataset.savedImageryZoom;delete tile.dataset.imageryUnavailable;delete tile.dataset.imagerySaved;
    releaseUrl();finish();report('detail');if(refreshQueued){refreshQueued=false;load(true);}
   };
   const failedLoad=()=>{if(cancelled)return;releaseUrl();pending=false;failed=true;tile.dataset.imageryUnavailable='true';if(!restoring&&!tile.firstChild)finish(new Error('Imagery unavailable.'));};
   image.onerror=failedLoad;
   if(refresh){
    // An already-open document can retain decoded pixels for the same image URL
    // even after worker revalidation. A fresh blob replaces only the loaded image.
    requestController=new AbortController();
    void fetch(this.getTileUrl(coords),{cache:'reload',signal:requestController.signal}).then(async response=>{
     if(!response.ok)throw Error('Imagery unavailable');const blob=await response.blob();if(cancelled)return;
     objectUrl=URL.createObjectURL(blob);image.src=objectUrl;
    }).catch(failedLoad);
   }else image.src=this.getTileUrl(coords);
  };
  this.work.set(tile,{url:new URL(this.getTileUrl(coords),location.origin).href,cancel:()=>{cancelled=true;requestController?.abort();releaseUrl();image.onload=null;image.onerror=null;image.src=L.Util.emptyImageUrl;},retry:force=>{if(force&&pending){refreshQueued=true;return;}if(force||failed||tile.dataset.imagerySaved){if(!tile.firstChild)finished=false;load(!!force);}}});
  // Race saved coverage against the network; replace only after image load.
  void (async()=>{
   try{
    for await(const saved of savedImageryCandidates(coords)){
     if(cancelled||ready)return;
     try{
      let decoding=this.decoded.get(saved.cacheKey);
      if(!decoding){
       decoding=decodeSavedImagery(saved.response);this.decoded.set(saved.cacheKey,decoding);
       if(this.decoded.size>32)this.decoded.delete(this.decoded.keys().next().value!);
       void decoding.catch(()=>this.decoded.delete(saved.cacheKey));
      }
      const decoded=await decoding;
      if(cancelled||ready)return;
      const canvas=document.createElement('canvas'),size=this.getTileSize();canvas.width=size.x;canvas.height=size.y;canvas.style.cssText='width:100%;height:100%';
      const context=canvas.getContext('2d');if(!context)break;
      const {x,y,size:portion}=saved.crop;
      context.drawImage(decoded,x*decoded.naturalWidth,y*decoded.naturalHeight,portion*decoded.naturalWidth,portion*decoded.naturalHeight,0,0,size.x,size.y);
      tile.dataset.savedImageryZoom=String(saved.zoom);tile.dataset.imagerySaved='true';tile.replaceChildren(canvas);finish();report(saved.zoom===coords.z?'saved':'wider-view');return;
     }catch{/* A corrupt tile may still have a usable saved ancestor. */}
    }
   }catch{/* Storage unavailable: the ordinary image request still completes. */}
   finally{restoring=false;if(failed&&!tile.firstChild)finish(new Error('No saved imagery covers this tile.'));}
  })();
  load();return tile;
 }
 retryMissing(){for(const [tile,work] of this.work)if(tile.isConnected)work.retry();}
 refreshSaved(url:string){this.decoded.delete(url);for(const [tile,work] of this.work)if(tile.isConnected&&work.url===url)work.retry(true);}
 hasMissing(){return [...this.work.keys()].some(tile=>tile.isConnected&&tile.dataset.imageryUnavailable&&!tile.firstChild);}
}

export default function OfflineImageryLayer(){
 const {map,layerContainer}=useLeafletContext();
 useEffect(()=>{
  const layer=new SavedImageryTileLayer(MAP_TILE_URLS.arcgisImagery,{maxZoom:19,keepBuffer:2,updateWhenIdle:true,updateWhenZooming:false,attribution:'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'});
  const notice=new L.Control({position:'topleft'}),button=document.createElement('button');
  button.type='button';button.className='map-imagery-notice';button.hidden=true;
  notice.onAdd=()=>{const wrapper=L.DomUtil.create('div','leaflet-control');wrapper.append(button);L.DomEvent.disableClickPropagation(wrapper);return wrapper;};notice.addTo(map);
  let timer:number|undefined;
  const status=()=>{window.clearTimeout(timer);const missing=layer.hasMissing();button.hidden=!missing;button.disabled=false;if(missing)button.textContent='Imagery unavailable · Retry';};
  const loading=()=>{window.clearTimeout(timer);timer=window.setTimeout(()=>{if(!map.getContainer().querySelector('.leaflet-tile-loaded canvas,.leaflet-tile-loaded img')){button.textContent='Loading imagery…';button.disabled=true;button.hidden=false;}},800);};
  button.onclick=()=>layer.retryMissing();layer.on('loading',loading);layer.on('load tileload tileerror tileunload',status);
  const container=layerContainer||map;container.addLayer(layer);
  const reconnect=()=>layer.retryMissing();window.addEventListener('online',reconnect);
  // A suspended installed app can miss the connectivity event. Resume repairs
  // only missing/degraded tiles; successful imagery stays mounted.
  const resume=()=>{if(document.visibilityState==='visible')layer.retryMissing();};
  document.addEventListener('visibilitychange',resume);
  const updated=(event:MessageEvent)=>{if(event.data?.type==='MAP_TILE_UPDATED'&&typeof event.data.url==='string')layer.refreshSaved(event.data.url);};
  navigator.serviceWorker?.addEventListener('message',updated);
  return()=>{window.clearTimeout(timer);window.removeEventListener('online',reconnect);document.removeEventListener('visibilitychange',resume);navigator.serviceWorker?.removeEventListener('message',updated);layer.off('loading',loading);layer.off('load tileload tileerror tileunload',status);notice.remove();container.removeLayer(layer);};
 },[map,layerContainer]);
 return null;
}
